import { useState, useEffect } from 'react'
import * as Sentry from "@sentry/react"
import LandingPage from './components/LandingPage'
import LandingChatScreen from './components/LandingChatScreen'
import AuthModal from './components/AuthModal'
import AuthCallback from './components/AuthCallback'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import { supabase } from './lib/supabase'

function App() {
  const [showApp, setShowApp] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthCallback, setIsAuthCallback] = useState(false)
  const [currentRoute, setCurrentRoute] = useState<string>('')

  // Check current route
  useEffect(() => {
    const path = window.location.pathname
    setCurrentRoute(path)
    
    if (path === '/auth/callback') {
      setIsAuthCallback(true)
    }
  }, [])

  // Check for existing session and listen to auth changes
  useEffect(() => {
    // Skip auth check for public pages
    if (currentRoute === '/privacy' || currentRoute === '/terms') {
      setIsLoading(false)
      return
    }

    // Check current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setShowApp(!!session)
      setIsLoading(false)
      // Set Sentry user context
      if (session?.user) {
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email,
        })
      } else {
        Sentry.setUser(null)
      }
    })

    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setShowApp(!!session)
      setShowAuthModal(false)
      setIsAuthCallback(false)
      // Update Sentry user context on auth changes
      if (session?.user) {
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email,
        })
      } else {
        Sentry.setUser(null)
      }
    })

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe()
  }, [currentRoute])

  // Test Sentry after a delay to ensure it's initialized
  useEffect(() => {
    // Test Sentry after 2 seconds (only in development)
    if (import.meta.env.MODE === 'development') {
      const timer = setTimeout(() => {
        console.log("🧪 Testing Sentry...");
        try {
          throw new Error("Test error for Sentry - " + new Date().toISOString());
        } catch (error) {
          Sentry.captureException(error, {
            tags: { test: true },
            level: 'info'
          });
          console.log("✅ Test error sent to Sentry! Check your Sentry dashboard.");
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [])

  // Handler for "Get Started" and "Login" buttons
  const handleOpenAuth = () => {
    setShowAuthModal(true)
  }

  // Handler for successful authentication
  const handleAuthenticate = () => {
    setShowAuthModal(false)
    setShowApp(true)
  }

  // Handler to close auth modal
  const handleCloseAuth = () => {
    setShowAuthModal(false)
  }

  // Handle auth callback success
  const handleAuthCallbackSuccess = () => {
    setIsAuthCallback(false)
    setShowApp(true)
    window.history.replaceState({}, '', '/')
  }

  // Show auth callback page when redirected from email link
  if (isAuthCallback) {
    return <AuthCallback onSuccess={handleAuthCallbackSuccess} />
  }

  // Show public pages (no auth required)
  if (currentRoute === '/privacy') {
    return <PrivacyPolicy />
  }

  if (currentRoute === '/terms') {
    return <TermsOfService />
  }

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <svg className="w-10 h-10 text-green-600 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {showApp ? (
        <LandingChatScreen />
      ) : (
        <>
          <LandingPage 
            onGetStarted={handleOpenAuth}
            onLogin={handleOpenAuth}
          />
          <AuthModal 
            isOpen={showAuthModal}
            onClose={handleCloseAuth}
            onAuthenticate={handleAuthenticate}
          />
        </>
      )}
    </>
  )
}

export default App
